import React, { ReactNode } from "react";
import { join, size } from "lodash";
import { Moment } from "moment-timezone";
import { formatDate } from "@backpacker/date";
import { tr } from "@linkfluence/i18n";
import {
  AbstractDate,
  Interval,
  PeriodId,
  getPeriodId,
} from "@linkfluence/models/date";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import {
  endOfDay,
  getDateFromDateMath,
  isDateMath,
  isSame,
  startOfDay,
  tz,
} from "@tootools/date";

interface IProps {
  from: AbstractDate;
  to: AbstractDate;
  label?: string;
  timezone: Timezone;
  locale: Locale;
  className?: string;
  nowDate?: AbstractDate;
}

const isEndOfDay = (date: AbstractDate): boolean =>
  isSame(endOfDay(date), date, "s");

const isStartOfDay = (date: AbstractDate): boolean =>
  isSame(startOfDay(date), date, "s");

const getFormatedDate = (
  date: AbstractDate,
  locale: Locale,
  interval: Interval
): string => {
  return formatDate(date, locale, interval === Interval.Hour ? "lll" : "ll");
};

const getFormatedFromTo = (
  from: Moment,
  to: Moment,
  locale: Locale
): string => {
  return join(
    [
      getFormatedDate(
        from,
        locale,
        isStartOfDay(from) ? Interval.Day : Interval.Hour
      ),
      getFormatedDate(
        to,
        locale,
        isEndOfDay(to) ? Interval.Day : Interval.Hour
      ),
    ],
    " - "
  );
};

export const getFormatedPeriodFromNow = (
  from: AbstractDate,
  locale: Locale,
  timezone: Timezone
): string => {
  const fromTz = tz(from, timezone);
  if (isAbstractDateString(from as string)) {
    return tr(locale, "DATES::PERIOD-FROM", {
      from: getFormatedDate(fromTz, locale, Interval.Day),
    });
  }
  return tr(locale, "DATES::PERIOD-FROM", {
    from: getFormatedDate(
      fromTz,
      locale,
      isStartOfDay(from) ? Interval.Day : Interval.Hour
    ),
  });
};

function getFormatedSameDay(
  from: AbstractDate,
  to: AbstractDate,
  locale: Locale,
  timezone: Timezone
): string {
  if (isStartOfDay(from) && isEndOfDay(to)) {
    return formatDate(from, locale, "ll");
  }
  const sameDayTo = isDateMath(to) ? getDateFromDateMath(to, timezone) : to;
  return join(
    [
      getFormatedDate(from, locale, Interval.Hour),
      formatDate(sameDayTo, locale, "LT"),
    ],
    " - "
  );
}

export const getFormatedPeriod = (
  from: AbstractDate,
  to: AbstractDate,
  locale: Locale,
  timezone: Timezone
): string => {
  const fromTz = tz(from, timezone);
  const toTz = tz(to, timezone);
  // Is same day
  return isSame(fromTz, toTz, "d")
    ? getFormatedSameDay(fromTz, toTz, locale, timezone)
    : getFormatedFromTo(fromTz, toTz, locale);
};

const isAbstractDateString = (dateString: string): boolean =>
  isNaN(Date.parse(dateString)); // returns a number if the string is a valid date, else, returns NaN

export const getFormatedDateLabel = ({
  from,
  to,
  label,
  timezone,
  locale,
  nowDate,
}: IProps): ReactNode => {
  if (nowDate) {
    return getFormatedDateLabel({
      from: getDateFromDateMathWithNow(from, nowDate, timezone, false),
      to: getDateFromDateMathWithNow(to, nowDate, timezone, true),
      label,
      timezone,
      locale,
    });
  } else {
    const periodId = getPeriodId(from, to);
    if (size(label)) {
      return label;
    }
    if (periodId === PeriodId.Custom) {
      return getFormatedPeriod(from, to, locale, timezone);
    }
    if (periodId === PeriodId.Custom_From) {
      return getFormatedPeriodFromNow(from, locale, timezone);
    }
    return tr(locale, `DATES::${periodId}`);
  }
};

function getDateFromDateMathWithNow(
  date: AbstractDate,
  now: AbstractDate,
  timezone: Timezone,
  roundUp: boolean
) {
  if (isDateMath(date)) {
    return getDateFromDateMath(date, timezone, roundUp, now);
  } else {
    return date;
  }
}

export default function UiKitFormatDate({ className, ...props }: IProps) {
  return <span className={className}>{getFormatedDateLabel(props)}</span>;
}
